import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/components/shared/ThemeProvider';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import { useSidebar } from '@/context/SidebarContext';
import { createActionsProvider } from './providers/createActionsProvider';
import { createPagesProvider } from './providers/createPagesProvider';
import type { CommandProvider } from './types';

interface CommandPaletteContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  providers: CommandProvider[];
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export function useCommandPalette() {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) {
    throw new Error('useCommandPalette must be used within CommandPaletteProvider');
  }
  return ctx;
}

export function CommandPaletteProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const app = useApp();
  const { logout } = useAuth();
  const { toggle: toggleSidebar } = useSidebar();
  const { setTheme, theme } = useTheme();

  // Use refs for all volatile deps to avoid provider recreation
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const toggleSidebarRef = useRef(toggleSidebar);
  toggleSidebarRef.current = toggleSidebar;
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  // Build providers once per app — use stable references via refs for volatile deps
  // biome-ignore lint/correctness/useExhaustiveDependencies: volatile deps (theme, navigate) use refs to avoid infinite re-renders
  const providers = useMemo(() => {
    const stableNavigate: typeof navigate = (...args: any[]) =>
      (navigateRef.current as any)(...args);

    const shared: CommandProvider[] = [
      createPagesProvider(app.navItems, stableNavigate),
      createActionsProvider({
        toggleSidebar: () => toggleSidebarRef.current(),
        toggleTheme: () => {
          setTheme(themeRef.current === 'dark' ? 'light' : 'dark');
        },
        logout: () => logoutRef.current(),
        navigate: stableNavigate,
      }),
    ];

    const appProviders = app.commandProviders?.(stableNavigate) ?? [];

    return [...shared, ...appProviders].sort((a, b) => a.priority - b.priority);
  }, [app.id]);

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const value = useMemo(() => ({ open, setOpen, providers }), [open, providers]);

  return <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>;
}
